<template>
  <b-modal
    :id="modalId"
    size="xl"
    title="Unificar cadastros de clientes"
    no-fade
    no-close-on-backdrop
    no-close-on-esc
    @hidden="onHidden"
    @show="onShow"
  >
    <div v-if="loading" class="text-center p-4">
      <b-spinner /><br />carregando prévia...
    </div>

    <div v-else-if="errorMsg" class="alert alert-danger">
      {{ errorMsg }}
    </div>

    <div v-else>
      <p class="text-muted">
        Escolha o cliente <strong>destino</strong>. Os outros serão marcados como
        <code>Unificado</code> e todos os pedidos, comandas, mesas, reservas e
        movimentações financeiras serão movidos para o destino. Saldos serão somados.
      </p>

      <div class="row">
        <div
          v-for="c in clientes"
          :key="c.cod_cliente"
          class="col-12 col-md-6 col-lg-4 mb-3"
        >
          <div
            class="card h-100"
            :class="{ 'border-success border-2 shadow': c.cod_cliente === codDestino }"
          >
            <div class="card-body">
              <div class="form-check mb-2">
                <input
                  class="form-check-input"
                  type="radio"
                  :id="'destino_' + c.cod_cliente"
                  :value="c.cod_cliente"
                  v-model="codDestino"
                />
                <label class="form-check-label font-weight-bold" :for="'destino_' + c.cod_cliente">
                  Usar como DESTINO
                </label>
              </div>
              <hr class="my-2" />
              <h6 class="mb-1">{{ c.Nome || '(sem nome)' }}</h6>
              <small class="text-muted d-block">#{{ c.cod_cliente }}</small>
              <small class="d-block"><strong>Tel:</strong> {{ c.Telefone || '—' }}</small>
              <small class="d-block"><strong>CPF/CNPJ:</strong> {{ c.CGC || '—' }}</small>
              <small class="d-block"><strong>Email:</strong> {{ c.Email || '—' }}</small>
              <small class="d-block"><strong>Endereço:</strong> {{ enderecoResumo(c) || '—' }}</small>
              <small class="d-block"><strong>Saldo:</strong> {{ formatFloatToReal(c.saldo) }}</small>
              <small class="d-block text-muted"><strong>Cadastro:</strong> {{ c.dt_cadastro ? $moment(c.dt_cadastro).format('DD/MM/YYYY') : '—' }}</small>
            </div>
          </div>
        </div>
      </div>

      <div v-if="preview && preview.success && preview.data" class="mt-3 p-3 bg-light rounded">
        <h6 class="mb-2">Prévia da unificação</h6>
        <ul class="mb-2">
          <li>
            Saldo no destino: {{ formatFloatToReal(preview.data.saldo_anterior) }}
            + {{ formatFloatToReal(preview.data.saldo_origens) }} (origens)
            = <strong>{{ formatFloatToReal(preview.data.saldo_novo) }}</strong>
          </li>
          <li>
            Referências a mover:
            <span v-for="(qtd, tab) in preview.data.referencias_a_mover" :key="tab" class="badge badge-secondary mr-1">
              {{ tab }}: {{ qtd }}
            </span>
          </li>
          <li v-if="Object.keys(preview.data.campos_a_copiar || {}).length > 0">
            Campos vazios no destino que serão preenchidos:
            <span v-for="(info, campo) in preview.data.campos_a_copiar" :key="campo" class="badge badge-info mr-1">
              {{ campo }} ← #{{ info.de }}
            </span>
          </li>
          <li v-else>Nenhum campo vazio para copiar (destino já tem tudo).</li>
        </ul>
        <small class="text-danger">
          <strong>Atenção:</strong> esta operação é irreversível.
        </small>
      </div>

      <div v-else-if="preview && !preview.success" class="alert alert-warning mt-3">
        {{ preview.message }}
      </div>
    </div>

    <template #modal-footer>
      <b-btn variant="secondary" @click="cancelar" :disabled="executando">Cancelar</b-btn>
      <b-btn
        variant="success"
        :disabled="!codDestino || executando || !preview || !preview.success"
        @click="confirmar"
      >
        <b-spinner small v-if="executando" /> Unificar
      </b-btn>
    </template>
  </b-modal>
</template>

<script>
import ClientesLib from "../../libs/ClientesLib";

export default {
  name: "ClienteUnificarModal",
  props: {
    modalId: { type: String, default: "modal-unificar-clientes" },
    clientes: { type: Array, required: true },
  },
  data() {
    return {
      loading: false,
      executando: false,
      errorMsg: null,
      codDestino: null,
      preview: null,
    };
  },
  watch: {
    codDestino() { this.carregarPreview(); },
  },
  methods: {
    enderecoResumo(c) {
      const partes = [c.Logradouro, c.Numero, c.Bairro, c.Cidade, c.UF].filter((x) => x && String(x).trim() !== "");
      return partes.join(", ");
    },
    onShow() {
      this.errorMsg = null;
      this.preview = null;
      this.executando = false;
      this.codDestino = this.escolherDestinoPadrao();
    },
    onHidden() {
      this.codDestino = null;
      this.preview = null;
      this.errorMsg = null;
      this.executando = false;
    },
    escolherDestinoPadrao() {
      if (!this.clientes || this.clientes.length === 0) return null;
      const ordenados = [...this.clientes].sort((a, b) => {
        const da = a.dt_cadastro ? new Date(a.dt_cadastro).getTime() : Infinity;
        const db = b.dt_cadastro ? new Date(b.dt_cadastro).getTime() : Infinity;
        return da - db;
      });
      return ordenados[0].cod_cliente;
    },
    async carregarPreview() {
      if (!this.codDestino) { this.preview = null; return; }
      const origens = this.clientes
        .map((c) => c.cod_cliente)
        .filter((id) => id !== this.codDestino);
      if (origens.length === 0) { this.preview = null; return; }
      this.loading = true;
      this.errorMsg = null;
      try {
        this.preview = await ClientesLib.previewUnificacao({
          cod_cliente_destino: this.codDestino,
          cod_cliente_origens: origens,
        });
      } catch (err) {
        this.errorMsg = "Erro ao carregar prévia: " + (err && err.message ? err.message : err);
      }
      this.loading = false;
    },
    cancelar() {
      this.$bvModal.hide(this.modalId);
    },
    async confirmar() {
      if (!this.codDestino || !this.preview || !this.preview.success) return;
      const origens = this.clientes
        .map((c) => c.cod_cliente)
        .filter((id) => id !== this.codDestino);
      const destinoNome = (this.clientes.find((c) => c.cod_cliente === this.codDestino) || {}).Nome || "destino";
      const confirm = await this.$swal.fire({
        title: "Confirmar unificação?",
        html:
          "Mesclar <b>" + origens.length + "</b> cliente(s) em <b>" + destinoNome + "</b>.<br>" +
          "<small class='text-danger'>Esta operação é irreversível.</small>",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, unificar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;

      this.executando = true;
      const result = await ClientesLib.unificar({
        cod_cliente_destino: this.codDestino,
        cod_cliente_origens: origens,
      });
      this.executando = false;

      if (result && result.success) {
        this.showToast("success", "Clientes unificados");
        this.$emit("unificado", result.data);
        this.$bvModal.hide(this.modalId);
      } else {
        this.$swal.fire({
          title: "Não foi possível unificar",
          text: (result && result.message) || "Erro desconhecido",
          icon: "error",
        });
      }
    },
  },
};
</script>
